import { Crosshair, Download } from "@procore/core-icons";
import { Box, Button, Checkbox, spacing, ToggleButton } from "@procore/core-react";
import { useEffect, useState } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { CameraType, renderActions, selectMainObject } from "features/render";

import { downloadBrep, getBrepBoundingSphere, hasBrep, setBrepDisplay, useBrepDisplay } from "./brep";
import { IdRow, Section, StatusTooltip } from "./common";
import { useActionStatus } from "./useActionStatus";

export function BrepSection() {
    const mainObject = useAppSelector(selectMainObject);
    const {
        state: { view },
    } = useExplorerGlobals(true);
    const [hasBrepState, setHasBrep] = useState<boolean>();
    const [brepError, setBrepError] = useState<string>();
    /** `null` when it could not be told, e.g. a scene without a measure view. */
    const [brepGenerated, setBrepGenerated] = useState<boolean | null>();
    const brepDisplay = useBrepDisplay();

    useEffect(() => () => setBrepDisplay({ show: false }), []);

    useEffect(() => {
        setHasBrep(undefined);
        setBrepError(undefined);
        setBrepGenerated(undefined);
        if (mainObject === undefined) {
            return;
        }
        let cancelled = false;
        hasBrep(view, mainObject).then(
            (has) => {
                if (cancelled) {
                    return;
                }
                setHasBrep(has);
                if (!has) {
                    return;
                }
                Promise.resolve(view.measure?.core.isParametricDataGenerated(mainObject))
                    .catch((e: unknown) => {
                        console.error("Failed to read whether the BREP is generated", e);
                        return undefined;
                    })
                    .then((generated) => !cancelled && setBrepGenerated(generated ?? null));
            },
            (e: unknown) => !cancelled && setBrepError(String(e)),
        );
        return () => {
            cancelled = true;
        };
    }, [mainObject, view]);

    return (
        <Section
            title="BREP"
            action={
                <Box style={{ display: "flex", gap: spacing.xs, alignItems: "center" }}>
                    <ZoomToBrepButton objectId={hasBrepState ? mainObject : undefined} />
                    <ToggleButton
                        size="sm"
                        selected={brepDisplay.show}
                        disabled={!hasBrepState}
                        onClick={() => setBrepDisplay({ show: !brepDisplay.show })}
                    >
                        Draw
                    </ToggleButton>
                </Box>
            }
        >
            <Box>
                <IdRow
                    label="BREP"
                    value={hasBrepState === undefined ? undefined : hasBrepState ? "Yes" : "No"}
                    placeholder={mainObject === undefined ? undefined : (brepError ?? "Loading…")}
                >
                    <DownloadBrepButton objectId={hasBrepState ? mainObject : undefined} />
                </IdRow>
                <IdRow
                    label="Generated"
                    value={brepGenerated == null ? undefined : brepGenerated ? "Yes" : "No"}
                    placeholder={hasBrepState && brepGenerated === undefined ? "Loading…" : undefined}
                />
            </Box>
            <Box style={{ display: "flex", flexWrap: "wrap", columnGap: spacing.lg }}>
                {/* Separate boxes: core-react adds a top margin to adjacent checkboxes. */}
                <Box>
                    <Checkbox
                        checked={brepDisplay.angles}
                        disabled={!brepDisplay.show}
                        onChange={(e) => setBrepDisplay({ angles: e.target.checked })}
                    >
                        Angles
                    </Checkbox>
                </Box>
                <Box>
                    <Checkbox
                        checked={brepDisplay.surfaces}
                        disabled={!brepDisplay.show}
                        onChange={(e) => setBrepDisplay({ surfaces: e.target.checked })}
                    >
                        Surfaces
                    </Checkbox>
                </Box>
                <Box>
                    <Checkbox
                        checked={brepDisplay.backFaces}
                        disabled={!brepDisplay.show || !brepDisplay.surfaces}
                        onChange={(e) => setBrepDisplay({ backFaces: e.target.checked })}
                    >
                        Back faces
                    </Checkbox>
                </Box>
            </Box>
        </Section>
    );
}

/** Saves the measure worker's copy of the BREP, the one Draw and Zoom use. */
function DownloadBrepButton({ objectId }: { objectId: number | undefined }) {
    const {
        state: { view },
    } = useExplorerGlobals();
    const [status, run, error] = useActionStatus();

    const download = () =>
        run(async () => {
            if (view && objectId !== undefined) {
                await downloadBrep(view, objectId);
            }
        }, "Failed to download BREP");

    return (
        <StatusTooltip status={status} doneText="Downloaded" error={error}>
            <Button
                icon={<Download />}
                variant="tertiary"
                size="sm"
                aria-label="Download BREP"
                disabled={objectId === undefined}
                loading={status === "running"}
                onClick={() => void download()}
            />
        </StatusTooltip>
    );
}

/** Fits the camera to the BREP itself rather than the rendered object, which a broken BREP may not match. */
function ZoomToBrepButton({ objectId }: { objectId: number | undefined }) {
    const {
        state: { view },
    } = useExplorerGlobals();
    const dispatch = useAppDispatch();
    const [status, run, error] = useActionStatus();

    const zoom = () =>
        run(async () => {
            if (view && objectId !== undefined) {
                const zoomTo = await getBrepBoundingSphere(view, objectId);
                dispatch(renderActions.setCamera({ type: CameraType.Pinhole, zoomTo }));
            }
        }, "Failed to zoom to BREP");

    return (
        <StatusTooltip status={status} doneText="Zoomed" error={error}>
            <Button
                variant="tertiary"
                size="sm"
                icon={<Crosshair />}
                aria-label="Zoom to BREP"
                title="Zoom to BREP"
                disabled={objectId === undefined}
                loading={status === "running"}
                onClick={() => void zoom()}
            />
        </StatusTooltip>
    );
}
